import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { dealsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import EditDealOffcanvas from '../components/EditDealOffcanvas';
import {
  ArrowLeft, DollarSign, Calendar, User, Building2, Target,
  Clock, TrendingUp, MessageSquare, Phone, Mail, FileText,
  Sparkles, Loader2, ChevronRight, ExternalLink, Package, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';

const STAGE_COLORS = {
  new: 'bg-blue-500/20 text-blue-400',
  qualified: 'bg-purple-500/20 text-purple-400',
  proposal: 'bg-amber-500/20 text-amber-400',
  negotiation: 'bg-orange-500/20 text-orange-400',
  won: 'bg-emerald-500/20 text-emerald-400',
  lost: 'bg-red-500/20 text-red-400',
};

export default function DealDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [coaching, setCoaching] = useState(null);
  const [comments, setComments] = useState([]);
  const [cockpit, setCockpit] = useState(null);
  const [cockpitFilter, setCockpitFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  const loadDeal = useCallback(async () => {
    try {
      const [dealRes, actRes, commRes] = await Promise.allSettled([
        dealsAPI.getOne(id),
        dealsAPI.getActivities(id),
        api.get(`/api/crm/deals/${id}/comments`),
      ]);
      if (dealRes.status === 'fulfilled') {
        const d = dealRes.value.data;
        setDeal(d);
        if (d.lead_id) {
          try {
            const lr = await api.get(`/api/leads/${d.lead_id}`);
            setLead(lr.data);
          } catch {}
        }
      }
      if (actRes.status === 'fulfilled') setActivities(actRes.value.data?.activities || actRes.value.data || []);
      if (commRes.status === 'fulfilled') setComments(commRes.value.data?.comments || commRes.value.data || []);
      // Phase 4: Deal Cockpit — Brain decisions tied to the channel timeline
      try {
        const ck = await api.get(`/api/deals/${id}/cockpit`);
        setCockpit(ck.data || null);
      } catch {}
      // Phase 4: try loading existing coaching from new endpoint first, then legacy
      try {
        const ch = await api.get(`/api/brain/deal-coaching/${id}`);
        if (ch.data?.coaching) setCoaching(ch.data.coaching);
      } catch {
        try {
          const lh = await api.get(`/api/crm/deals/${id}/coaching-history`);
          if (lh.data?.history?.length) setCoaching(lh.data.history[0]);
        } catch {}
      }
      // Pre-cached briefing on deal doc (from earlier call, if any)
      try {
        if (dealRes.status === 'fulfilled' && dealRes.value.data?.pre_call_briefing) {
          setBriefing(dealRes.value.data.pre_call_briefing);
        }
      } catch {}
    } catch { toast.error('Failed to load deal'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadDeal(); }, [loadDeal]);

  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  const generateCoaching = async () => {
    setCoachingLoading(true);
    try {
      const res = await api.post(`/api/brain/deal-coaching/${id}`);
      // Phase 4 fix: endpoint now returns {status:"queued", job_id} — poll GET for result
      const priorCoachedAt = deal?.last_coached_at || null;
      if (res.data?.status === 'queued') {
        toast.info('AI coaching queued — generating (this takes ~60s)');
        // Poll every 4s for up to 3 minutes
        const start = Date.now();
        const TIMEOUT_MS = 180000;
        const POLL_MS = 4000;
        const poll = async () => {
          if (Date.now() - start > TIMEOUT_MS) {
            toast.error('Coaching is taking longer than expected. Refresh the page in a moment.');
            setCoachingLoading(false);
            return;
          }
          try {
            const dr = await dealsAPI.getOne(id);
            const d = dr.data;
            // Fresh coaching if last_coached_at changed
            if (d?.last_coached_at && d.last_coached_at !== priorCoachedAt) {
              setDeal(d);
              try {
                const ch = await api.get(`/api/brain/deal-coaching/${id}`);
                if (ch.data?.coaching) setCoaching(ch.data.coaching);
              } catch {}
              toast.success('AI coaching ready');
              setCoachingLoading(false);
              return;
            }
          } catch {}
          setTimeout(poll, POLL_MS);
        };
        setTimeout(poll, POLL_MS);
        return;
      }
      // Back-compat path (in case endpoint ever runs synchronously)
      setCoaching(res.data?.coaching || res.data);
      try {
        const dr = await dealsAPI.getOne(id);
        setDeal(dr.data);
      } catch {}
      toast.success('AI coaching generated');
      setCoachingLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate coaching');
      setCoachingLoading(false);
    }
  };

  const generatePreCallBriefing = async () => {
    setBriefingLoading(true);
    try {
      const res = await api.post(`/api/brain/deal-coaching/${id}/pre-call-briefing`);
      setBriefing(res.data);
      toast.success('Briefing ready');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate briefing');
    } finally { setBriefingLoading(false); }
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    try {
      await api.post(`/api/crm/deals/${id}/comments`, { content: commentText });
      setCommentText('');
      const res = await api.get(`/api/crm/deals/${id}/comments`);
      setComments(res.data?.comments || res.data || []);
    } catch { toast.error('Failed to add comment'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="deal-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="text-center py-20" data-testid="deal-not-found">
        <p className="text-muted-foreground">Deal not found</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/deals')}>
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to Deals
        </Button>
      </div>
    );
  }

  const stageColor = STAGE_COLORS[deal.stage?.toLowerCase()] || STAGE_COLORS.new;
  const probability = deal.probability || (deal.stage === 'won' ? 100 : deal.stage === 'lost' ? 0 : 50);

  return (
    <div className="space-y-6" data-testid="deal-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/deals')} data-testid="deal-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold" data-testid="deal-name">
              {deal.deal_name || 'Unnamed Deal'}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`${stageColor} border-0 text-[10px]`} data-testid="deal-stage">
                {deal.stage || 'New'}
              </Badge>
              {deal.priority && (
                <Badge variant="outline" className="text-[10px]" data-testid="deal-priority">
                  {deal.priority}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-2xl font-bold" data-testid="deal-value">
              ${(deal.total || deal.subtotal || 0).toLocaleString()}
            </p>
            <p className="text-[11px] text-muted-foreground">{probability}% probability</p>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditOpen(true)} data-testid="deal-edit-btn">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left column: Deal info + Line Items */}
        <div className="lg:col-span-2 space-y-4">
          {/* Overview */}
          <Card className="border border-border/50" data-testid="deal-overview-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Deal Overview</CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <InfoRow icon={DollarSign} label="Value" value={`$${(deal.total || 0).toLocaleString()}`} />
              <InfoRow icon={Target} label="Stage" value={deal.stage || 'New'} />
              <InfoRow icon={Calendar} label="Expected Close" value={deal.expected_close_date ? new Date(deal.expected_close_date).toLocaleDateString() : 'Not set'} />
              <InfoRow icon={User} label="Assigned To" value={deal.assigned_to_name || deal.assigned_to || 'Unassigned'} />
              <InfoRow icon={Building2} label="Source" value={deal.source || 'N/A'} />
              <InfoRow icon={TrendingUp} label="Probability" value={`${probability}%`} />
              {deal.description && (
                <div className="sm:col-span-2">
                  <p className="text-[11px] text-muted-foreground mb-1">Description</p>
                  <p className="text-sm">{deal.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Products & Services */}
          <Card className="border border-border/50" data-testid="deal-products-services">
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Package className="w-4 h-4 text-muted-foreground" /> Products &amp; Services
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1" onClick={() => setEditOpen(true)} data-testid="deal-manage-products-btn">
                <Pencil className="w-3 h-3" /> {deal.line_items?.length > 0 ? 'Edit' : 'Add'}
              </Button>
            </CardHeader>
            <CardContent>
              {deal.line_items?.length > 0 ? (
                <>
                  <div className="text-[11px] text-muted-foreground grid grid-cols-4 gap-2 pb-2 border-b border-border/30 mb-2">
                    <span>Item</span><span className="text-right">Qty</span><span className="text-right">Price</span><span className="text-right">Total</span>
                  </div>
                  {deal.line_items.map((item, i) => (
                    <div key={i} className="grid grid-cols-4 gap-2 py-1.5 text-sm" data-testid={`deal-line-item-${i}`}>
                      <span className="truncate">{item.name || item.description || 'Item'}</span>
                      <span className="text-right">{item.quantity}</span>
                      <span className="text-right">${(item.price ?? item.unit_price ?? 0)?.toLocaleString()}</span>
                      <span className="text-right font-medium">${(item.total ?? ((item.quantity || 0) * (item.price ?? item.unit_price ?? 0)))?.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="grid grid-cols-4 gap-2 pt-2 mt-2 border-t border-border/30 text-sm font-medium">
                    <span className="col-span-3 text-right">Total:</span>
                    <span className="text-right">${(deal.total || deal.subtotal || 0).toLocaleString()}</span>
                  </div>
                </>
              ) : (
                <div className="text-center py-6" data-testid="deal-products-empty">
                  <Package className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">No products or services added to this deal yet.</p>
                  <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => setEditOpen(true)} data-testid="deal-add-products-btn">
                    <Pencil className="w-3.5 h-3.5" /> Add Products &amp; Services
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stage History */}
          {deal.stage_history?.length > 0 && (
            <Card className="border border-border/50" data-testid="deal-stage-history">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Stage History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative pl-4">
                  {deal.stage_history.map((sh, i) => (
                    <div key={i} className="relative pb-4 last:pb-0">
                      <div className="absolute left-[-12px] top-1.5 w-2 h-2 rounded-full bg-primary" />
                      {i < deal.stage_history.length - 1 && <div className="absolute left-[-9px] top-4 w-[2px] h-full bg-border/50" />}
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[9px]">{sh.from_stage || '—'}</Badge>
                        <ChevronRight className="w-3 h-3 text-muted-foreground" />
                        <Badge className={`${STAGE_COLORS[sh.to_stage?.toLowerCase()] || ''} border-0 text-[9px]`}>{sh.to_stage}</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {sh.changed_at ? new Date(sh.changed_at).toLocaleString() : ''}
                        {sh.reason ? ` — ${sh.reason}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Phase 4: Deal Cockpit — Brain decisions ↔ channel timeline */}
          <Card className="border border-border/50" data-testid="deal-cockpit-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-500" />
                Deal Cockpit
                {cockpit?.timeline?.length ? (
                  <span className="text-muted-foreground font-normal">({cockpit.timeline.length})</span>
                ) : null}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!cockpit || (cockpit.timeline || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4" data-testid="cockpit-empty">
                  No Brain decisions or channel activity yet for this deal.
                </p>
              ) : (
                <>
                <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="cockpit-filters">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'brain', label: 'Brain' },
                    { key: 'email', label: 'Email' },
                    { key: 'voice', label: 'Voice' },
                    { key: 'meeting', label: 'Meeting' },
                  ].map(f => {
                    const n = f.key === 'all' ? cockpit.timeline.length : cockpit.timeline.filter(i => i.channel === f.key).length;
                    return (
                      <button
                        key={f.key}
                        onClick={() => setCockpitFilter(f.key)}
                        data-testid={`cockpit-filter-${f.key}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${cockpitFilter === f.key ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-transparent text-muted-foreground border-border hover:bg-muted'}`}
                      >
                        {f.label} <span className="opacity-70">{n}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="space-y-3 max-h-[440px] overflow-y-auto" data-testid="cockpit-timeline">
                  {cockpit.timeline.filter(i => cockpitFilter === 'all' || i.channel === cockpitFilter).slice(0, 40).map((item, i) => {
                    const isAI = item.actor === 'ai';
                    const isLead = item.actor === 'lead';
                    const icon = item.channel === 'brain' ? <Sparkles className="w-3.5 h-3.5 text-indigo-500" /> :
                      item.channel === 'email' ? <Mail className="w-3.5 h-3.5" /> :
                      item.channel === 'voice' ? <Phone className="w-3.5 h-3.5" /> :
                      item.channel === 'meeting' ? <Calendar className="w-3.5 h-3.5" /> :
                      <Clock className="w-3.5 h-3.5" />;
                    return (
                      <div key={i} className="flex items-start gap-2.5 pb-3 border-b border-border/20 last:border-0" data-testid={`cockpit-item-${i}`}>
                        <div className={`h-7 w-7 rounded flex items-center justify-center shrink-0 mt-0.5 ${isAI ? 'bg-indigo-500/10' : isLead ? 'bg-emerald-500/10' : 'bg-muted/50'}`}>
                          {icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium truncate">{item.title}</span>
                            <Badge variant="outline" className="text-[9px] capitalize">{item.channel}</Badge>
                            {isAI && <Badge className="bg-indigo-500/10 text-indigo-600 border-0 text-[9px]">AI</Badge>}
                            {isLead && <Badge className="bg-emerald-500/10 text-emerald-600 border-0 text-[9px]">Lead</Badge>}
                          </div>
                          {item.detail ? (
                            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{item.detail}</p>
                          ) : null}
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Activities */}
          <Card className="border border-border/50" data-testid="deal-activities">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Activity Timeline ({activities.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No activities yet</p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {activities.slice(0, 20).map((act, i) => (
                    <div key={i} className="flex items-start gap-2.5 pb-3 border-b border-border/20 last:border-0">
                      <div className="h-7 w-7 rounded bg-muted/50 flex items-center justify-center shrink-0 mt-0.5">
                        {act.type === 'email' ? <Mail className="w-3.5 h-3.5" /> :
                         act.type === 'call' ? <Phone className="w-3.5 h-3.5" /> :
                         act.type === 'stage_change' ? <TrendingUp className="w-3.5 h-3.5" /> :
                         <Clock className="w-3.5 h-3.5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm">{act.description || act.title || act.type}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {act.created_at ? new Date(act.created_at).toLocaleString() : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column: Lead Info + Coaching + Comments */}
        <div className="space-y-4">
          {/* Linked Lead */}
          {lead && (
            <Card className="border border-border/50" data-testid="deal-linked-lead">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <User className="w-3.5 h-3.5" /> Linked Lead
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="font-medium text-sm">{lead.name || 'Unknown'}</p>
                  {lead.email && <p className="text-[11px] text-muted-foreground">{lead.email}</p>}
                  {lead.company && <p className="text-[11px]">{lead.company}</p>}
                  {lead.title && <p className="text-[11px] text-muted-foreground">{lead.title}</p>}
                  <div className="flex items-center gap-2 mt-2">
                    {lead.ai_score != null && (
                      <Badge variant="outline" className="text-[9px]">AI Score: {lead.ai_score}</Badge>
                    )}
                    {lead.ai_qualification && (
                      <Badge variant="secondary" className="text-[9px]">{lead.ai_qualification}</Badge>
                    )}
                  </div>
                  <Link to={`/leads/${lead.id}`} className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline mt-2">
                    View Lead <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Phase 4: AI Deal Coaching — shows denormalized deal.win_probability/next_best_action + full coaching */}
          <Card className="border border-border/50" data-testid="deal-coaching-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 justify-between">
                <span className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> AI Deal Coaching
                </span>
                {deal.win_probability != null && (
                  <Badge variant="outline" className="text-[10px]" data-testid="deal-win-prob">
                    {deal.win_probability}% win
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Hot fields from deal doc (cheap read, always fresh) */}
              {(deal.next_best_action || deal.coaching_summary || deal.risk_level) && (
                <div className="space-y-2 pb-2 border-b border-border/30">
                  {deal.next_best_action && (
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Next best action</p>
                      <p className="text-[12px] font-medium text-foreground">{deal.next_best_action}</p>
                    </div>
                  )}
                  {deal.coaching_summary && (
                    <p className="text-[11px] text-muted-foreground italic">{deal.coaching_summary}</p>
                  )}
                  {deal.risk_level && deal.risk_level !== 'low' && deal.risk_flags?.length > 0 && (
                    <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2">
                      <p className="text-[10px] font-semibold text-amber-400 uppercase mb-1">
                        Risk: {deal.risk_level}
                      </p>
                      {deal.risk_flags.slice(0, 3).map((r, i) => (
                        <p key={i} className="text-[11px] text-amber-300">• {r}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Full coaching (from deal_coaching collection) */}
              {coaching ? (
                <div className="space-y-2">
                  {coaching.recommended_actions?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Recommended actions</p>
                      {coaching.recommended_actions.slice(0, 3).map((a, i) => (
                        <div key={i} className="text-[11px] leading-snug mb-1">
                          <span className="font-medium">{a.action || a}</span>
                          {a.reason && <span className="text-muted-foreground"> — {a.reason}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {coaching.talking_points?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Talking points</p>
                      {coaching.talking_points.slice(0, 3).map((t, i) => (
                        <p key={i} className="text-[11px]">• {t}</p>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                !deal.coaching_summary && (
                  <p className="text-[11px] text-muted-foreground">No AI coaching yet. Click to generate.</p>
                )
              )}

              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 text-[11px]" onClick={generateCoaching} disabled={coachingLoading} data-testid="refresh-coaching-btn">
                  {coachingLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  {coaching || deal.coaching_summary ? 'Refresh' : 'Generate coaching'}
                </Button>
                <Button variant="outline" size="sm" className="flex-1 text-[11px]" onClick={generatePreCallBriefing} disabled={briefingLoading} data-testid="pre-call-briefing-btn">
                  {briefingLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Phone className="w-3 h-3 mr-1" />}
                  Pre-call brief
                </Button>
              </div>

              {/* Pre-call briefing card (only rendered after user clicks) */}
              {briefing && (
                <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-3 space-y-2" data-testid="pre-call-briefing-card">
                  {briefing.headline && (
                    <p className="text-[12px] font-semibold text-foreground">{briefing.headline}</p>
                  )}
                  {briefing.why_this_fits_icp && (
                    <p className="text-[11px] text-muted-foreground italic">{briefing.why_this_fits_icp}</p>
                  )}
                  {briefing.top_objections?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Top objections</p>
                      {briefing.top_objections.slice(0, 3).map((o, i) => (
                        <div key={i} className="text-[11px] mb-1">
                          <span className="font-medium">• {o.objection}</span>
                          {o.handling && <span className="text-muted-foreground"> → {o.handling}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {briefing.questions_to_ask?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase mb-0.5">Ask</p>
                      {briefing.questions_to_ask.slice(0, 3).map((q, i) => (
                        <p key={i} className="text-[11px]">• {q}</p>
                      ))}
                    </div>
                  )}
                  {briefing.close_the_call_with && (
                    <div className="pt-1 border-t border-border/30">
                      <p className="text-[10px] text-muted-foreground uppercase">End with</p>
                      <p className="text-[11px] font-medium">{briefing.close_the_call_with}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <Card className="border border-border/50" data-testid="deal-comments">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5" /> Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[200px] overflow-y-auto mb-3">
                {comments.length === 0 && (
                  <p className="text-[11px] text-muted-foreground text-center py-2">No comments yet</p>
                )}
                {comments.map((c, i) => (
                  <div key={i} className="p-2 rounded bg-muted/30 text-sm">
                    <p>{c.content}</p>
                    <p className="text-[9px] text-muted-foreground mt-1">
                      {c.created_by_name || 'User'} — {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  className="flex-1 text-sm bg-muted/30 border border-border/50 rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Add comment..."
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addComment()}
                  data-testid="deal-comment-input"
                />
                <Button size="sm" onClick={addComment} data-testid="deal-comment-submit">Post</Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border border-border/50" data-testid="deal-actions">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" size="sm" className="w-full justify-start text-[11px]"
                onClick={async () => {
                  try {
                    await dealsAPI.convertToQuote(id);
                    toast.success('Quote created from deal');
                  } catch { toast.error('Failed to create quote'); }
                }}
                data-testid="convert-to-quote-btn"
              >
                <FileText className="w-3.5 h-3.5 mr-2" /> Convert to Quote
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <EditDealOffcanvas
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        deal={deal}
        onSuccess={() => { setEditOpen(false); loadDeal(); }}
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <div>
        <p className="text-[10px] text-muted-foreground">{label}</p>
        <p className="text-sm">{value}</p>
      </div>
    </div>
  );
}
